import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Guards against accidental navigation when there are unsaved changes.
 *
 * - Registers a `beforeunload` listener to warn on browser close / tab refresh.
 * - Intercepts `history.pushState` (used by Wouter's setLocation) so in-app
 *   navigation can be blocked and confirmed via a dialog.
 *
 * Usage:
 *   const { pendingNav, confirmLeave, cancelLeave } = useUnsavedChanges(isDirty);
 *   // Render a <ConfirmDialog open={pendingNav !== null} onConfirm={confirmLeave} onCancel={cancelLeave} />
 */
export function useUnsavedChanges(isDirty: boolean) {
  const [pendingNav, setPendingNav] = useState<string | null>(null);
  const origPushStateRef = useRef<typeof history.pushState | null>(null);
  const pendingArgsRef = useRef<Parameters<typeof history.pushState> | null>(
    null
  );

  // Browser close / reload guard
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  // In-app navigation guard via history.pushState interception
  useEffect(() => {
    if (!isDirty) return;

    const orig = window.history.pushState.bind(window.history);
    origPushStateRef.current = orig;

    window.history.pushState = function (
      ...args: Parameters<typeof history.pushState>
    ) {
      const url = args[2];
      const newPath =
        typeof url === "string"
          ? new URL(url, window.location.href).pathname
          : null;

      // Allow same-path pushes (query param / hash updates)
      if (newPath === window.location.pathname) {
        orig(...args);
        return;
      }

      // Different route — show confirmation dialog
      pendingArgsRef.current = args;
      setPendingNav(newPath ?? String(url));
    };

    return () => {
      if (origPushStateRef.current) {
        window.history.pushState = origPushStateRef.current;
        origPushStateRef.current = null;
      }
    };
  }, [isDirty]);

  /** User confirmed leaving — complete the intercepted navigation. */
  const confirmLeave = useCallback(() => {
    const orig = origPushStateRef.current;
    const args = pendingArgsRef.current;
    setPendingNav(null);
    pendingArgsRef.current = null;
    if (orig && args) {
      orig(...args);
    }
  }, []);

  /** User cancelled — stay on the current page. */
  const cancelLeave = useCallback(() => {
    setPendingNav(null);
    pendingArgsRef.current = null;
  }, []);

  return { pendingNav, confirmLeave, cancelLeave };
}
